import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { createUserStorage } from './userStorage'

const RECENTS_STORE_KEY = 'recents-store'
const MAX_RECENT_ITEMS = 10

export interface RecentProject {
  type: 'project'
  projectId: string
  spaceId: string
  companyId: string
  name: string
  lastOpenedAt: number
}

export interface RecentSpace {
  type: 'space'
  spaceId: string
  companyId: string
  name: string
  lastOpenedAt: number
}

export type RecentItem = RecentProject | RecentSpace

interface RecentsStoreState {
  recentItems: RecentItem[]
}

const INITIAL_RECENTS_STORE: RecentsStoreState = {
  recentItems: [],
}

const useRecentsStore = create<RecentsStoreState>()(
  persist(() => INITIAL_RECENTS_STORE, {
    name: RECENTS_STORE_KEY,
    storage: createUserStorage(),
  }),
)

export const trackRecentProject = (
  projectId: string,
  spaceId: string,
  companyId: string,
  name: string,
) => {
  useRecentsStore.setState((state) => {
    const filteredItems = state.recentItems.filter(
      (existing) =>
        !(existing.type === 'project' && existing.projectId === projectId),
    )

    const newItem: RecentProject = {
      type: 'project',
      projectId,
      spaceId,
      companyId,
      name,
      lastOpenedAt: Date.now(),
    }

    return {
      recentItems: [newItem, ...filteredItems].slice(0, MAX_RECENT_ITEMS),
    }
  })
}

export const trackRecentSpace = (
  spaceId: string,
  companyId: string,
  name: string,
) => {
  useRecentsStore.setState((state) => {
    const filteredItems = state.recentItems.filter(
      (existing) =>
        !(existing.type === 'space' && existing.spaceId === spaceId),
    )

    const newItem: RecentSpace = {
      type: 'space',
      spaceId,
      companyId,
      name,
      lastOpenedAt: Date.now(),
    }

    return {
      recentItems: [newItem, ...filteredItems].slice(0, MAX_RECENT_ITEMS),
    }
  })
}

export const removeRecentItems = ({
  projectId,
  spaceId,
  companyId,
}: {
  projectId?: string
  spaceId?: string
  companyId?: string
}) => {
  useRecentsStore.setState((state) => ({
    recentItems: state.recentItems.filter(
      (item) =>
        !(
          (companyId && item.companyId === companyId) ||
          (spaceId && item.spaceId === spaceId) ||
          (projectId && item.type === 'project' && item.projectId === projectId)
        ),
    ),
  }))
}

export const useRecentItems = (): RecentItem[] =>
  useRecentsStore((state) => state.recentItems)

export const rehydrateRecentsStore = () => useRecentsStore.persist.rehydrate()

export const resetRecentsStore = () =>
  useRecentsStore.setState({ recentItems: [] }, true)
